import { useEffect, useState } from "react";
import { Ticket, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { btnBlue, btnGhost, card, Field, inp, Tag } from "@/components/club/ui";

const eur = (n) => `${Number(n).toFixed(2).replace(".", ",")} €`;
const fecha = (s) => new Date(s).toLocaleDateString("es-ES");
const state = (p) => (p.void ? ["Anulado", "gray"] : new Date(p.expires_at) < new Date() ? ["Caducado", "gray"] : p.remaining ? ["Activo", "green"] : ["Agotado", "red"]);

const PassCard = ({ p, onVoid }) => {
  const [label, tone] = state(p);
  return (
    <li className={`${card} flex flex-wrap items-center gap-4`} data-testid={`pass-${p.id}`}>
      <Ticket className="text-[#B23A38]" />
      <div className="flex-1 min-w-[160px]" translate="no">
        <p className="font-bold text-[#0B1D3A]">{p.name}{onVoid && <span className="font-normal text-slate-500"> · {p.user_name}</span>}</p>
        <p className="text-sm text-slate-600">Válido hasta {fecha(p.expires_at)}{onVoid ? ` · ${eur(p.price)} · vendido por ${p.sold_by}` : ""}</p>
      </div>
      <p className="font-display font-black text-2xl text-[#0B1D3A]" data-testid={`pass-remaining-${p.id}`}>{p.remaining}<span className="text-base text-slate-400">/{p.total}</span></p>
      <Tag tone={tone}>{label}</Tag>
      {onVoid && !p.void && <button onClick={() => onVoid(p.id)} className="text-slate-400 hover:text-[#B23A38]" aria-label="Anular" data-testid={`pass-void-${p.id}`}><Trash2 size={18} /></button>}
    </li>
  );
};

export const MyPasses = ({ clubId }) => {
  const [mine, setMine] = useState(null);
  const [types, setTypes] = useState([]);
  useEffect(() => {
    api.get(`/club/clubs/${clubId}/passes/me`).then((r) => setMine(r.data)).catch(() => setMine([]));
    api.get(`/club/clubs/${clubId}/pass-types`).then((r) => setTypes(r.data));
  }, [clubId]);
  if (!mine) return <p className="text-slate-500">Cargando…</p>;
  return (
    <div className="space-y-6" data-testid="my-passes">
      <div>
        <h3 className="font-display font-black uppercase text-[#3D88B8] mb-2">Mis bonos</h3>
        {mine.length ? <ul className="space-y-3">{mine.map((p) => <PassCard key={p.id} p={p} />)}</ul>
          : <p className={`${card} text-slate-600`} data-testid="my-passes-empty">Aún no tienes bonos. Sin bono puedes reservar y pagar la clase suelta en el club.</p>}
      </div>
      {!!types.length && (
        <div>
          <h3 className="font-display font-black uppercase text-[#3D88B8] mb-2">Bonos del club</h3>
          <div className="grid sm:grid-cols-3 gap-3">{types.map((t) => (
            <div key={t.id} className={card} data-testid={`pass-type-card-${t.id}`} translate="no">
              <p className="font-bold text-lg">{t.name}</p>
              <p className="text-slate-600">{t.classes} clases · {t.days} días de validez</p>
              <p className="font-display font-black text-2xl text-[#B23A38] mt-2">{eur(t.price)}</p>
            </div>))}</div>
          <p className="text-sm text-slate-500 mt-2">Los bonos se compran y pagan en el club; el administrador te los activa al momento. Cada reserva descuenta una clase, y si cancelas con 24 h de antelación la recuperas.</p>
        </div>
      )}
    </div>
  );
};

const TypeForm = ({ clubId, onDone }) => {
  const empty = { name: "", classes: 10, price: "", days: 90 };
  const [f, setF] = useState(empty);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "number" ? Number(e.target.value) : e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post(`/club/clubs/${clubId}/pass-types`, f); toast.success("Bono creado"); setF(empty); onDone(); } catch (e2) { toast.error(errMsg(e2)); }
  };
  return (
    <form onSubmit={submit} className={`${card} grid sm:grid-cols-4 gap-4`} data-testid="pass-type-form">
      <Field label="Nombre"><input required value={f.name} onChange={set("name")} placeholder="Bono 10 clases" className={inp} data-testid="pass-type-name" /></Field>
      <Field label="Nº de clases"><input type="number" min="1" required value={f.classes} onChange={set("classes")} className={inp} data-testid="pass-type-classes" /></Field>
      <Field label="Precio (€)"><input type="number" min="0" step="0.01" required value={f.price} onChange={set("price")} className={inp} data-testid="pass-type-price" /></Field>
      <Field label="Validez (días)"><input type="number" min="1" required value={f.days} onChange={set("days")} className={inp} data-testid="pass-type-days" /></Field>
      <button className={`${btnBlue} sm:col-span-4`} data-testid="pass-type-create">Crear tipo de bono</button>
    </form>
  );
};

export const ManagePasses = ({ clubId, members }) => {
  const [types, setTypes] = useState([]);
  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState({ user_id: "", type_id: "" });
  const load = () => {
    api.get(`/club/clubs/${clubId}/pass-types`).then((r) => setTypes(r.data));
    api.get(`/club/clubs/${clubId}/passes`).then((r) => setRows(r.data));
  };
  useEffect(() => { load(); }, [clubId]); // eslint-disable-line react-hooks/exhaustive-deps
  const assign = async (e) => {
    e.preventDefault();
    try { await api.post(`/club/clubs/${clubId}/passes`, sel); toast.success("Bono activado (cobrado en el club)"); setSel({ user_id: "", type_id: "" }); load(); } catch (e2) { toast.error(errMsg(e2)); }
  };
  const removeType = async (id) => { await api.delete(`/club/pass-types/${id}`); load(); };
  const voidPass = async (id) => { if (window.confirm("¿Anular este bono? Las clases restantes se perderán.")) { await api.post(`/club/passes/${id}/void`); load(); } };
  const students = members.filter((m) => m.role !== "public");
  return (
    <div className="space-y-5" data-testid="manage-passes">
      <TypeForm clubId={clubId} onDone={load} />
      {!!types.length && <div className="flex flex-wrap gap-2">{types.map((t) => (
        <span key={t.id} className="inline-flex items-center gap-2 bg-slate-100 rounded-full pl-4 pr-2 py-1.5 text-sm font-bold" data-testid={`pass-type-${t.id}`} translate="no">
          {t.name} · {t.classes} clases · {eur(t.price)} · {t.days} d
          <button onClick={() => removeType(t.id)} aria-label="Retirar" className="text-slate-400 hover:text-[#B23A38]" data-testid={`pass-type-delete-${t.id}`}><Trash2 size={14} /></button>
        </span>))}</div>}
      <form onSubmit={assign} className={`${card} grid sm:grid-cols-3 gap-4 items-end`} data-testid="pass-assign-form">
        <Field label="Alumno"><select required value={sel.user_id} onChange={(e) => setSel({ ...sel, user_id: e.target.value })} className={inp} data-testid="pass-assign-member">
          <option value="">Elige…</option>{students.map((m) => <option key={m.user_id} value={m.user_id}>{m.name}</option>)}</select></Field>
        <Field label="Bono"><select required value={sel.type_id} onChange={(e) => setSel({ ...sel, type_id: e.target.value })} className={inp} data-testid="pass-assign-type">
          <option value="">Elige…</option>{types.map((t) => <option key={t.id} value={t.id}>{t.name} ({eur(t.price)})</option>)}</select></Field>
        <button className={btnGhost} data-testid="pass-assign-submit">Vender bono (pagado en el club)</button>
      </form>
      <ul className="space-y-3">{rows.map((p) => <PassCard key={p.id} p={p} onVoid={voidPass} />)}</ul>
    </div>
  );
};
